import type { ReactNode } from 'react'
import { createFileRoute, Link } from '@tanstack/react-router'
import { Check, CircleCheck, TriangleAlert } from 'lucide-react'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import { useTasks, useCompleteTask, type Task } from '@/hooks/use-tasks'
import { usePipelineFunnel } from '@/hooks/use-pipeline'
import { useAccountsList } from '@/hooks/use-accounts'
import { useModuleAvailability } from '@/hooks/use-module-availability'
import { splitTasksByDue } from '@/lib/today'
import {
  ACTIVE_STAGES,
  STAGE_CONFIG,
  formatCurrency,
  localDateISO,
  summarizeByStage,
} from '@/lib/pipeline'

// The "Today" workspace: a bounded surface answering "what needs my attention
// today?" composed ONLY from existing read queries and mutation hooks. No new
// server semantics, tables, or columns. Every widget has a designed empty state.
export const Route = createFileRoute('/_authed/dashboard')({
  component: TodayWorkspace,
})

function TodayWorkspace() {
  const now = new Date()
  const prettyDate = now.toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  })

  // The pipeline widget links into the Pipeline module, so only show it when
  // the shared status-aware resolver answers 'available' (R6) — the same rule
  // the nav uses, so the home never advertises a destination the workspace
  // hides, and an unresolved read never renders the widget.
  const { availability: pipelineAvailability } = useModuleAvailability('pipeline')
  const pipelineAvailable = pipelineAvailability === 'available'

  return (
    <div className="p-6 space-y-6">
      {/* Slice 1B-1: "Today" is titled by the ONE shared header; the page
          keeps only the contextual date line. */}
      <header>
        <p className="text-[13px] text-[color:var(--ink-3)]">{prettyDate}</p>
      </header>

      <div className="grid gap-4 lg:grid-cols-2">
        <TasksTodayCard />
        {pipelineAvailable && <PipelineSummaryCard />}
      </div>

      <RecentAccountsCard />
    </div>
  )
}

/* ---------- Tasks due today + overdue ---------- */

function TasksTodayCard() {
  const { data, isLoading, isError } = useTasks()
  const complete = useCompleteTask()
  const todayISO = localDateISO(new Date())
  const { overdue, dueToday } = splitTasksByDue(data ?? [], todayISO)

  return (
    <Card data-testid="today-tasks">
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <CardTitle className="text-[15px]">Due &amp; overdue</CardTitle>
        <Link to="/tasks" className="text-[12px] text-[color:var(--accent-text)] underline">
          All tasks
        </Link>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <WidgetSkeleton rows={3} />
        ) : isError ? (
          <WidgetError what="tasks" />
        ) : overdue.length === 0 && dueToday.length === 0 ? (
          <EmptyLine
            icon={<CircleCheck className="h-4 w-4" aria-hidden="true" />}
            text="Nothing due today — you're clear. New tasks appear here on their due date."
          />
        ) : (
          <ul className="space-y-1">
            {overdue.map((t) => (
              <TaskRow key={t.id} task={t} overdue onComplete={() => complete.mutate(t.id)} />
            ))}
            {dueToday.map((t) => (
              <TaskRow key={t.id} task={t} onComplete={() => complete.mutate(t.id)} />
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  )
}

function TaskRow({
  task,
  overdue = false,
  onComplete,
}: {
  task: Task
  overdue?: boolean
  onComplete: () => void
}) {
  return (
    <li className="flex items-center gap-2 rounded-md px-1 py-1 hover:bg-[color:var(--surface-2)]">
      <Button
        variant="ghost"
        size="icon"
        className="h-6 w-6 shrink-0"
        aria-label={`Complete task: ${task.title}`}
        onClick={onComplete}
      >
        <Check className="h-4 w-4" />
      </Button>
      {overdue && (
        <TriangleAlert
          className="h-3.5 w-3.5 shrink-0 text-[color:var(--status-lost-fg)]"
          aria-label="Overdue"
        />
      )}
      <span className="min-w-0 flex-1 truncate text-[13px]">{task.title}</span>
    </li>
  )
}

/* ---------- Pipeline summary by stage ---------- */

function PipelineSummaryCard() {
  const { data, isLoading, isError } = usePipelineFunnel()
  const rows = data?.rows ?? []
  const byStage = summarizeByStage(rows, ACTIVE_STAGES)
  const hasAny = byStage.some((s) => s.count > 0)

  return (
    <Card data-testid="today-pipeline">
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <CardTitle className="text-[15px]">Active pipeline</CardTitle>
        <Link to="/pipeline" className="text-[12px] text-[color:var(--accent-text)] underline">
          Open pipeline
        </Link>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <WidgetSkeleton rows={3} />
        ) : isError ? (
          <WidgetError what="pipeline" />
        ) : !hasAny ? (
          <EmptyLine text="No active deals yet. Deals you create appear here by stage." />
        ) : (
          <>
            <ul className="space-y-0.5">
              {byStage.map(({ stage, count, total }) => (
                <li key={stage}>
                  <Link
                    to="/pipeline"
                    search={{ stage }}
                    className="flex items-center justify-between gap-2 rounded-md px-2 py-1.5 hover:bg-[color:var(--surface-2)]"
                  >
                    <span className="flex items-center gap-2 text-[13px]">
                      <span
                        className="h-2.5 w-2.5 rounded-full"
                        style={{ backgroundColor: STAGE_CONFIG[stage].fg }}
                        aria-hidden="true"
                      />
                      {STAGE_CONFIG[stage].label}
                    </span>
                    <span className="tabular-nums [font-feature-settings:'tnum'_1] text-[12px] text-[color:var(--ink-3)]">
                      {count} {count === 1 ? 'deal' : 'deals'} · {formatCurrency(total)}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
            {data?.capped && (
              <p className="mt-2 text-[12px] text-[color:var(--ink-3)]">
                Totals cover the first {rows.length} deals.
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}

/* ---------- Recently touched accounts ---------- */

function RecentAccountsCard() {
  const { data, isLoading, isError } = useAccountsList({
    pageIndex: 0,
    pageSize: 5,
    sortBy: 'updated_at',
    sortDir: 'desc',
    showInactive: false,
    scope: 'all',
  })
  const rows = data?.rows ?? []

  return (
    <Card data-testid="today-accounts">
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <CardTitle className="text-[15px]">Recently updated accounts</CardTitle>
        <Link to="/accounts" className="text-[12px] text-[color:var(--accent-text)] underline">
          All accounts
        </Link>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <WidgetSkeleton rows={5} />
        ) : isError ? (
          <WidgetError what="accounts" />
        ) : rows.length === 0 ? (
          <EmptyLine text="No accounts yet. Create one from Accounts to start tracking relationships." />
        ) : (
          <ul className="divide-y divide-[color:var(--line)]">
            {rows.map((a) => (
              <li key={a.id}>
                <Link
                  to="/accounts/$accountId"
                  params={{ accountId: a.id }}
                  className="flex items-center justify-between gap-2 px-2 py-2 hover:bg-[color:var(--surface-2)]"
                >
                  <span className="min-w-0 flex-1 truncate text-[13px]">{a.name}</span>
                  {a.customer_ref && (
                    <span className="font-mono text-[11px] text-[color:var(--ink-3)]">
                      {a.customer_ref}
                    </span>
                  )}
                  {(a.city || a.state) && (
                    <span className="shrink-0 text-[12px] text-[color:var(--ink-3)]">
                      {[a.city, a.state].filter(Boolean).join(', ')}
                    </span>
                  )}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  )
}

/* ---------- Shared bits ---------- */

function WidgetSkeleton({ rows }: { rows: number }) {
  return (
    <div className="space-y-2" role="status" aria-live="polite" aria-label="Loading">
      {Array.from({ length: rows }).map((_, i) => (
        <Skeleton key={i} className="h-6 w-full" />
      ))}
    </div>
  )
}

function WidgetError({ what }: { what: string }) {
  return (
    <p className="text-[13px] text-[color:var(--status-lost-fg)]">
      Couldn’t load {what} right now. Reload to try again.
    </p>
  )
}

function EmptyLine({ icon, text }: { icon?: ReactNode; text: string }) {
  return (
    <div className="flex items-center gap-2 py-2 text-[13px] text-[color:var(--ink-3)]">
      {icon}
      <span>{text}</span>
    </div>
  )
}
